import React from 'react';
import { SlideFooter } from '../components/canvas/SlideFooter';
import { TITLE_FONT_SIZE_LEVELS, DEFAULT_TITLE_FONT_SIZE, BODY_FONT_SIZE_LEVELS, DEFAULT_BODY_FONT_SIZE, KICKER_FONT_SIZE_LEVELS, DEFAULT_KICKER_FONT_SIZE, SUBTITLE_FONT_SIZE_LEVELS, DEFAULT_SUBTITLE_FONT_SIZE, STATS_FONT_SIZE_LEVELS, DEFAULT_STATS_FONT_SIZE, TAGS_FONT_SIZE_LEVELS, DEFAULT_TAGS_FONT_SIZE } from '../constants/typography';
import { renderFormattedText } from '../utils/formatText';
import { usePreviewInteraction } from '../contexts/PreviewInteractionContext';
import { InlineEditable } from '../components/canvas/InlineEditable';
import { NudgeableGroup } from '../components/canvas/NudgeableGroup';
import { MiniStat } from '../components/canvas/Widgets';

/**
 * Normies Full BG layout — The Normie IS the background.
 *
 * The hero image is rendered by ForestBackground (background layer)
 * when normies.enabled is true. This template only adds:
 * - Bottom gradient overlay for text readability
 * - Text overlay zone in bottom portion
 * Text colors driven by the palette system (pc prop).
 */
export function NormiesFullBgLayout({ data, fontPreset, pc }) {
  const { interactive, onElementClick, editingField, onEditChange, onEndEdit } = usePreviewInteraction();

  const titleSize = TITLE_FONT_SIZE_LEVELS[data.titleFontSize ?? DEFAULT_TITLE_FONT_SIZE]?.size || 74;
  const bodySize = BODY_FONT_SIZE_LEVELS[data.bodyFontSize ?? DEFAULT_BODY_FONT_SIZE]?.size || 32;
  const kickerSize = KICKER_FONT_SIZE_LEVELS[data.kickerFontSize ?? DEFAULT_KICKER_FONT_SIZE]?.size || 16;
  const subtitleSize = SUBTITLE_FONT_SIZE_LEVELS[data.subtitleFontSize ?? DEFAULT_SUBTITLE_FONT_SIZE]?.size || 20;
  const statsSize = STATS_FONT_SIZE_LEVELS[data.statsFontSize ?? DEFAULT_STATS_FONT_SIZE]?.size || 20;
  const tagsSize = TAGS_FONT_SIZE_LEVELS[data.tagsFontSize ?? DEFAULT_TAGS_FONT_SIZE]?.size || 11;

  const handleTitleClick = (e) => {
    if (!interactive || !onElementClick) return;
    if (editingField === 'title') return;
    e.stopPropagation();
    onElementClick('header', 'title');
  };

  const handleBodyClick = (e) => {
    if (!interactive || !onElementClick) return;
    e.stopPropagation();
    onElementClick('text', 'subtitle');
  };

  return (
    <>
      {/* Bottom gradient overlay for text readability — sits on top of the
          Normie hero image rendered by ForestBackground */}
      <div
        className="absolute inset-x-0 bottom-0"
        style={{
          height: '55%',
          background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Content area — text overlay */}
      <NudgeableGroup groupId="content" className="absolute bottom-[140px] left-[64px] right-[64px]" style={{ maxHeight: '500px' }}>
        {/* Kicker */}
        {data.kickerEnabled !== false && data.kicker && (
          <div
            style={{
              fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
              fontSize: `${kickerSize}px`,
              fontWeight: 600,
              letterSpacing: '0.15em',
              color: pc.el.kicker || pc.textMuted,
              textTransform: 'uppercase',
              marginBottom: '12px',
            }}
          >
            {data.kicker}
          </div>
        )}

        {/* Title */}
        <InlineEditable
          value={data.title}
          isEditing={editingField === 'title'}
          onChange={(html) => onEditChange && onEditChange('title', html)}
          onBlur={onEndEdit}
          className={`leading-[1.0] w-full${editingField === 'title' ? ' preview-editing' : (interactive ? ' preview-clickable' : '')}`}
          style={{
            fontSize: `${Math.max(titleSize, 64)}px`,
            fontFamily: fontPreset.title,
            fontWeight: fontPreset.titleWeight || 700,
            color: pc.el.title || pc.textPrimary,
            textTransform: 'uppercase',
            letterSpacing: '-0.02em',
          }}
          onClick={handleTitleClick}
        >
          {renderFormattedText(data.title)}
        </InlineEditable>

        {/* Subtitle */}
        {data.subtitleEnabled !== false && data.subtitle && (
          <InlineEditable
            value={data.subtitle || ''}
            isEditing={editingField === 'subtitle'}
            onChange={(text) => onEditChange && onEditChange('subtitle', text)}
            onBlur={onEndEdit}
            plainText
            className={`mt-2${editingField === 'subtitle' ? ' preview-editing' : (interactive ? ' preview-clickable' : '')}`}
            style={{
              fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
              fontSize: `${subtitleSize}px`,
              color: pc.el.subtitle || pc.textMuted,
            }}
            onClick={handleBodyClick}
          >
            {data.subtitle}
          </InlineEditable>
        )}

        {/* Body */}
        {data.bodyEnabled !== false && data.body && (
          <div
            className="mt-3"
            style={{
              fontFamily: fontPreset.body || 'IBM Plex Mono, monospace',
              fontSize: `${bodySize}px`,
              lineHeight: 1.5,
              color: pc.el.body || pc.textSubtle,
            }}
          >
            {renderFormattedText(data.body)}
          </div>
        )}

        {/* Stats row */}
        {data.stats && data.stats.length > 0 && (
          <div className="flex gap-4 mt-4">
            {data.stats.map((stat, i) => (
              <MiniStat key={i} stat={stat} pc={pc} fontSize={statsSize} />
            ))}
          </div>
        )}

        {/* Tags */}
        {data.tagsEnabled !== false && data.meta && data.meta.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {data.meta.map((tag, i) => (
              <span
                key={i}
                style={{
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: `${tagsSize}px`,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  color: pc.el.tagText || pc.textMuted,
                  border: `1px solid ${pc.el.tagBorder || pc.accent(0.30)}`,
                  padding: '3px 10px',
                  textTransform: 'uppercase',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </NudgeableGroup>

      {/* Footer */}
      <div className="absolute bottom-[40px] left-[64px] right-[64px]">
        <SlideFooter data={data} pc={pc} />
      </div>
    </>
  );
}
